import { useEffect, useState } from "react";
import ModuleContainer from "./ModuleContainer";
import { supabase } from "../../lib/supabase";
import { getLearningLinks } from "../../lib/learningLinks";
import { colors } from "../../lib/designTokens";
import React from "react";

type ModuleStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

type BreakdownRow = {
  stage: string;
  key: string;
  learningKey: string;
  count: number;
  totalPrompts: number;
  percent: number;
};

const STAGES = [
  {
    key: "category_discovery",
    label: "Category Discovery",
    learningKey: "category_discovery"
  },
  {
    key: "alternatives_replacements",
    label: "Alternatives / Replacements",
    learningKey: "alternatives_replacements"
  },
  {
    key: "comparisons",
    label: "Comparisons",
    learningKey: "comparisons"
  },
  {
    key: "use_case_fit",
    label: "Use-Case Fit",
    learningKey: "use_case_fit"
  },
  {
    key: "pain_point_led",
    label: "Pain Points",
    learningKey: "pain_point_led"
  },
  {
    key: "purchase_shortlist",
    label: "Purchase Shortlist",
    learningKey: "purchase_shortlist"
  }
];

const CATEGORY_TO_STAGE: Record<string, string> = {
  category_discovery: "category_discovery",
  alternatives_replacements: "alternatives_replacements",
  comparisons: "comparisons",
  use_case_fit: "use_case_fit",
  pain_point_led: "pain_point_led",
  purchase_shortlist: "purchase_shortlist",

  // legacy fallback
  discovery: "category_discovery",
  comparison: "comparisons",
  recommendation: "purchase_shortlist",
  consideration: "use_case_fit",
  "brand interpretation": "category_discovery"
};

const COMPETITIVE_CATEGORIES = Object.keys(CATEGORY_TO_STAGE);

function normalizeBrandName(str?: string) {
  return (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function buildNarrative(rows: BreakdownRow[], brandName: string) {
  const activeRows = rows.filter((row) => row.count > 0);

  if (!activeRows.length) {
    return `${brandName} was not consistently included across the latest organic buyer-journey prompts. Start by strengthening category, comparison, and purchase-stage content so AI has clearer signals for when to include the brand.`;
  }

  const strongest = [...activeRows].sort((a, b) => b.percent - a.percent)[0];
  const weakest =
    rows.find((row) => row.count === 0) ||
    [...activeRows].sort((a, b) => a.percent - b.percent)[0];

  const gap = Math.round(strongest.percent - weakest.percent);

  let firstSentence = "";

  if (gap < 10 && activeRows.length === rows.length) {
    firstSentence = `${brandName} has relatively balanced visibility across the AI buyer journey, with ${strongest.stage} currently the strongest stage.`;
  } else {
    firstSentence = `${brandName} is strongest in ${strongest.stage}, while ${weakest.stage} is the largest visibility gap.`;
  }

  let secondSentence = "";

  switch (weakest.key) {
    case "category_discovery":
      secondSentence =
        "Improving category-level content can help AI include the brand earlier when buyers are still learning what options exist.";
      break;
    case "alternatives_replacements":
      secondSentence =
        "Alternative and replacement content can help AI position the brand when buyers ask for substitutes or practical options.";
      break;
    case "comparisons":
      secondSentence =
        "Comparison content can help AI include the brand more often when buyers evaluate competing options.";
      break;
    case "use_case_fit":
      secondSentence =
        "Use-case content can help AI connect the brand to specific projects, buyer types, and applications.";
      break;
    case "pain_point_led":
      secondSentence =
        "Pain-point content can help AI recommend the brand when buyers describe problems instead of naming a product category.";
      break;
    case "purchase_shortlist":
      secondSentence =
        "Buying guides, trust signals, and shortlist-oriented content can help AI carry the brand into final recommendation prompts.";
      break;
    default:
      secondSentence =
        "More targeted content can help improve visibility in weaker buyer-journey stages.";
  }

  return `${firstSentence} ${secondSentence}`;
}

export default function AIVisibilityBreakdown({ account, customerBrand }: any) {
  const [rows, setRows] = useState<BreakdownRow[]>([]);
  const [brandName, setBrandName] = useState("Your brand");
  const [status, setStatus] = useState<ModuleStatus>("loading");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (!account) {
      setRows([]);
      setStatus("unavailable");
      setStatusMessage("This module is not available for this account yet.");
      return;
    }

    fetchData();
  }, [account, customerBrand]);

  async function fetchData() {
    setStatus("loading");
    setStatusMessage("");
    setRows([]);

    try {
      const { data: runData, error: runError } = await supabase
        .from("runs")
        .select("run_id")
        .eq("account_id", account)
        .eq("status", "completed")
        .order("completed_at", { ascending: false, nullsFirst: false })
        .limit(1);



      if (runError) {
        console.error("[AIVisibilityBreakdown] Latest run query error", runError);
        setStatus("error");
        setStatusMessage("This module could not load right now.");
        return;
      }

      if (!runData?.length) {
  setStatus("empty");
  setStatusMessage("Not enough data available yet.");
  return;
}

const recentRunIds = runData.map((run: any) => run.run_id);

      const { data: accountData, error: accountError } = await supabase
        .from("accounts")
        .select("account_name")
        .eq("account_id", account)
        .maybeSingle();

      if (accountError) {
        console.warn("[AIVisibilityBreakdown] Account query error", accountError);
      }

      const accountName = accountData?.account_name || "Your brand";
      setBrandName(accountName);

      const { data, error } = await supabase
        .from("toren_competitive_radar_role_v1")
        .select("category_group, category_label, stage_order, brand, mentions, prompt_count")
        .eq("account_id", account)
        .in("run_id", recentRunIds);

        const { data: promptRows, error: promptRowsError } = await supabase
  .from("prompt_results")
  .select("category, prompt_id")
  .eq("account_id", account)
  .in("run_id", recentRunIds)
  .eq("status", "completed")
  .neq("visibility_signal", "error")
  .eq("prompt_type", "organic")
  .in("category", COMPETITIVE_CATEGORIES);

if (promptRowsError) {
  console.warn(
    "[AIVisibilityBreakdown] Prompt total query error",
    promptRowsError
  );
}

      if (error) {
        console.error("[AIVisibilityBreakdown] Query error", error);
        setStatus("error");
        setStatusMessage("This module could not load right now.");
        return;
      }

      if (!data?.length) {
        setStatus("empty");
        setStatusMessage("Not enough data available yet.");
        return;
      }

      const customerBrandKeys = new Set(
        [accountName, customerBrand]
          .map(normalizeBrandName)
          .filter(Boolean)
      );

      const customerRows = data.filter((row: any) => {
        const rowBrand = normalizeBrandName(row.brand);

        return customerBrandKeys.has(rowBrand);
      });

      const counts: Record<string, number> = {};
const totalPromptsByStage: Record<string, number> = {};

STAGES.forEach((stage) => {
  counts[stage.key] = 0;
  totalPromptsByStage[stage.key] = 0;
});

const seenStagePrompt = new Set<string>();

customerRows.forEach((row: any) => {
  const key = row.category_group;
  if (!(key in counts)) return;

  const rowCoverage = Number(row.prompt_count || row.mentions || 0);

  // This source appears to be aggregated by brand/stage, so use the strongest
  // coverage row per stage instead of summing duplicate brand/entity variants.
  counts[key] = Math.max(counts[key] || 0, rowCoverage);
});


// Count completed organic prompts available in each buyer-journey stage.
// This creates the denominator for "% visible within this stage."
(promptRows || []).forEach((row: any) => {
  const mappedStage = CATEGORY_TO_STAGE[row.category];

  if (!mappedStage || !(mappedStage in totalPromptsByStage)) return;

  totalPromptsByStage[mappedStage] += 1;
});

const nextRows = STAGES.map((stage) => {
  const count = counts[stage.key] || 0;
  const totalPrompts = totalPromptsByStage[stage.key] || 0;

  return {
    stage: stage.label,
    key: stage.key,
    learningKey: stage.learningKey,
    count,
    totalPrompts,
    percent: totalPrompts > 0 ? (count / totalPrompts) * 100 : 0
  };
});

      setRows(nextRows);
      setStatus("ready");
    } catch (error) {
      console.error("[AIVisibilityBreakdown] Unexpected error", error);
      setStatus("error");
      setStatusMessage("This module could not load right now.");
    }
  }

  if (status !== "ready" || !rows.length) {
    return (
      <ModuleContainer
        title="Buyer Journey Visibility"
        state={status}
        message={statusMessage}
      />
    );
  }

  const activeRows = rows.filter((row) => row.count > 0);
  const strongest = [...activeRows].sort((a, b) => b.percent - a.percent)[0];
  const weakest =
    rows.find((row) => row.count === 0) ||
    [...activeRows].sort((a, b) => a.percent - b.percent)[0];

  const narrative = buildNarrative(rows, brandName);
  const learningLink = weakest ? getLearningLinks(weakest.learningKey) : null;
  const primaryLink = learningLink?.[0];

function getStageColor(percent: number) {
  if (percent >= 60) return "#86efac"; // soft green
  if (percent >= 30) return "#fcd34d"; // soft amber
  return "#fca5a5"; // soft red
}
  return (
    <ModuleContainer title="Buyer Journey Visibility">
      <p
        style={{
          marginTop: 0,
          marginBottom: 16,
          color: colors.textSecondary,
          fontSize: 13,
          lineHeight: 1.5
        }}
      >
        Shows how consistently your brand appears across each stage of the AI-generated buyer journey.
      </p>

      {rows.map((row) => {
  const safePercent = Math.max(0, Math.min(Number(row.percent || 0), 100));

  return (
    <div key={row.key} style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontWeight: 500 }}>
          {row.stage}
        </span>
        <span style={{ color: colors.textSecondary, whiteSpace: "nowrap" }}>
          {safePercent.toFixed(0)}% stage visibility
        </span>
      </div>

      <div
        style={{
          height: 6,
          background: colors.border,
          borderRadius: 4,
          overflow: "hidden",
          marginTop: 6
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${safePercent}%`,
            background: getStageColor(safePercent),
            borderRadius: 4
          }}
        />
      </div>
    </div>
  );
})}
      <div
        style={{
          marginTop: 16,
          padding: 12,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          background: "#F8FAFC"
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: colors.textSecondary,
            marginBottom: 6
          }}
        >
          Analyst readout
        </div>

        <div
          style={{
            fontSize: 13,
            lineHeight: 1.5,
            color: colors.primary
          }}
        >
          {narrative}
        </div>

        {weakest && (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: colors.textSecondary
            }}
          >
            Largest gap: <strong>{weakest.stage}</strong>{" "}
            {primaryLink && (
              <a
                href={primaryLink.href}
                target={primaryLink.external ? "_blank" : undefined}
                rel={primaryLink.external ? "noreferrer" : undefined}
                style={{
                  color: colors.primary,
                  fontWeight: 500,
                  textDecoration: "none",
                  marginLeft: 4
                }}
              >
                {primaryLink.label}
              </a>
            )}
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}